'use client';

/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */
import { useOptionalUserProfile } from '@/lib/providers/user-profile-provider';
import { useLawFirmProfile } from '@/lib/services/account/hooks';
import { AppSidebar } from './app-sidebar';
import { navigationData as defaultNavigationData } from './navigation-data';
import { ScaleIcon } from 'lucide-react';
import type { ComponentProps } from 'react';

/**
 * Authenticated AppSidebar that uses shared user profile context and law firm branding
 * Prevents duplicate profile fetches
 */
export function AuthenticatedAppSidebar(
  props: Omit<
    ComponentProps<typeof AppSidebar>,
    'navigationData' | 'brandName' | 'isLoadingBranding' | 'logo'
  >
) {
  const { profile, displayName, isLoading } = useOptionalUserProfile();
  const { profile: lawFirmProfile, isLoading: isLoadingFirm } =
    useLawFirmProfile();

  // Create navigation data with actual user info or null if loading
  const navigationData = {
    ...defaultNavigationData,
    user: profile
      ? {
          name: displayName || 'User',
          email: profile.email || 'user@example.com',
          avatar: profile.avatar
        }
      : null,
    isLoadingUser: isLoading
  };

  // Get favicon from law firm profile which has the faviconUrl field
  const faviconUrl = lawFirmProfile?.faviconUrl;
  const firmName = lawFirmProfile?.name || profile?.lawFirm?.name;

  // Create logo element - either favicon or fallback icon
  const logo = faviconUrl ? (
    <img
      src={faviconUrl}
      alt={`${firmName} logo`}
      className="h-5 w-5"
      onError={(e) => {
        // If favicon fails to load, hide it and show nothing
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  ) : (
    <ScaleIcon className="h-5 w-5" />
  );

  // Only show loading skeleton during initial load when there's no cached data
  // This prevents flashing when the cache is invalidated but data exists
  const showLoadingSkeleton =
    (isLoadingFirm || isLoading) && !lawFirmProfile && !profile;

  return (
    <AppSidebar
      navigationData={navigationData}
      brandName={firmName}
      logo={logo}
      isLoadingBranding={showLoadingSkeleton}
      {...props}
    />
  );
}
